import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowRight, Rocket, X, WarningCircle, Lightning } from "@phosphor-icons/react";

const FEATURES = {
  solo_onetime: (p) => [`${p.seats} user`, `${p.cards_per_user} AI ID Card`, `${p.ai_max} AI max`, "One-time payment"],
  small_onetime: (p) => [`${p.seats} registered email`, `${p.cards_per_user} AI ID Cards`, `Up to ${p.ai_max} AIs`, "One-time payment"],
  medium_onetime: (p) => [`${p.seats} registered emails`, `${p.cards_per_user} AI ID Cards per user`, `${p.ai_max} AIs per user (${p.seats * p.ai_max} total)`, "Invite teammates"],
  enterprise_onetime: (p) => [`${p.seats} registered emails`, `${p.cards_per_user} AI ID Cards per user`, `${p.ai_max} AIs per user (${p.seats * p.ai_max} total)`, "Invite teammates"],
};

export default function Store() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [warroom, setWarroom] = useState(null);
  const [simonProducts, setSimonProducts] = useState([]);
  const [confirmProduct, setConfirmProduct] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/meta/plans").then((r) => {
      setPlans(r.data.plans);
      setWarroom(r.data.warroom);
      setSimonProducts(r.data.simon_products || []);
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const buy = async (lookup_key) => {
    if (!user) {
      navigate("/register");
      return;
    }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(null);
    }
  };

  const simonBuy = async (product_key) => {
    if (!user) { navigate("/register"); return; }
    setBusy(product_key);
    try {
      const { data } = await api.post("/payments/simon-checkout", {
        product_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(null);
    }
  };

  const startSubscription = (p) => { setAgreed(false); setConfirmProduct(p); };
  const confirmSubscription = () => {
    if (!agreed || !confirmProduct) return;
    const pk = confirmProduct.product_key;
    setConfirmProduct(null);
    simonBuy(pk);
  };

  const subs = simonProducts.filter((p) => p.type === "subscription");
  const topups = simonProducts.filter((p) => p.type === "topup");
  const buildUnlock = simonProducts.find((p) => p.type === "build_unlock");

  const highlight = "medium_onetime";

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20">
        <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-3">// AI iD Card</p>
        <h1 className="font-mono-p font-bold uppercase tracking-tight text-3xl sm:text-4xl">
          The AI iD Card
        </h1>
        <p className="mt-3 text-sm text-zinc-500 max-w-xl">
          The AI iD Card is an encrypted, copy-protected identity &amp; continuity record for your AI — it preserves your AI's role, scope, and memory so it never loses context across sessions, loops, or resets. Explore the available tiers and get your AI iD Card at{" "}
          <a href="https://aicp.getdigmoon.com" className="text-cyan-400 hover:underline">aicp.getdigmoon.com</a>.
        </p>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p, i) => {
            const isHi = p.lookup_key === highlight;
            return (
              <div
                key={p.lookup_key}
                data-testid={`plan-${p.lookup_key}`}
                className={`relative flex flex-col border p-7 fadeup ${
                  isHi ? "border-cyan-400/50 bg-[#0b0e13]" : "border-zinc-800 bg-[#090A0C]"
                }`}
                style={{ animationDelay: `${i * 50}ms` }}
              >
                {isHi && (
                  <span className="absolute -top-px right-0 bg-cyan-400 text-black font-mono-p text-[10px] uppercase tracking-widest px-2 py-1">
                    Popular
                  </span>
                )}
                <h3 className="font-mono-p font-bold uppercase tracking-tight text-lg">{p.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-mono-p font-extrabold text-4xl text-cyan-400">${p.price}</span>
                  <span className="text-zinc-600 text-sm mb-1">once</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {FEATURES[p.lookup_key](p).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-400">
                      <Check size={16} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`buy-${p.lookup_key}`}
                  onClick={() => buy(p.lookup_key)}
                  disabled={busy === p.lookup_key}
                  className={`mt-7 flex items-center justify-center gap-2 font-mono-p text-sm uppercase tracking-widest py-3 font-bold transition-colors disabled:opacity-50 ${
                    isHi
                      ? "bg-cyan-400 text-black hover:bg-cyan-300"
                      : "border border-zinc-700 text-zinc-200 hover:border-cyan-400/50 hover:text-cyan-400"
                  }`}
                >
                  {busy === p.lookup_key ? "Redirecting..." : <>Get Started <ArrowRight size={15} weight="bold" /></>}
                </button>
              </div>
            );
          })}
        </div>

        {/* S.I.M.O.N. subscription */}
        {warroom && (
          <div className="mt-10 border border-amber-400/30 bg-amber-400/5 p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <img src="/simon-logo.png" alt="S.I.M.O.N." className="w-16 h-auto object-contain mt-0.5 shrink-0" />
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-mono-p font-bold uppercase tracking-tight text-xl">{warroom.name}</h3>
                  <span data-testid="simon-live-badge" className="font-mono-p text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-400/40 px-2 py-1">
                    SIMON is here
                  </span>
                </div>
                <p className="mt-2 text-sm text-zinc-400 max-w-xl">
                  A collaborative workspace where you (the human-in-the-loop) and your AI collaborators communicate and work together in real time. Your AIs act as collaborators — augmenting and filling the gaps in your skill set — across one shared command center of up to 5 AIs. <span className="text-emerald-400 font-semibold">SIMON is here. Subscribe or see pricing below.</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <button
                data-testid="buy-warroom"
                onClick={() => document.getElementById("simon-tiers")?.scrollIntoView({ behavior: "smooth" })}
                className="font-mono-p text-sm uppercase tracking-widest bg-amber-400 text-black px-6 py-3 font-bold hover:bg-amber-300 transition-colors"
              >
                Subscribe
              </button>
            </div>
          </div>
        )}

        {/* S.I.M.O.N. subscription tiers */}
        {subs.length > 0 && (
          <div id="simon-tiers" className="mt-16 scroll-mt-24">
            <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-3">// S.I.M.O.N. Subscriptions</p>
            <h2 className="font-mono-p font-bold uppercase tracking-tight text-2xl sm:text-3xl">Monthly API tiers</h2>
            <p className="mt-2 text-sm text-zinc-500 max-w-xl">Recurring monthly subscriptions that power your S.I.M.O.N. AI usage. Cancel anytime.</p>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {subs.map((p, i) => (
                <div key={p.product_key} data-testid={`simon-sub-${p.product_key}`} className="relative flex flex-col border border-cyan-400/30 bg-[#0b0e13] p-7 fadeup" style={{ animationDelay: `${i * 50}ms` }}>
                  <h3 className="font-mono-p font-bold uppercase tracking-tight text-lg">{p.name}</h3>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="font-mono-p font-extrabold text-4xl text-cyan-400">${p.price}</span>
                    <span className="text-zinc-600 text-sm mb-1">/mo</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" /> {p.credits} API credits / month</li>
                    <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" /> Full S.I.M.O.N. command center</li>
                    <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" /> Recurring monthly billing</li>
                  </ul>
                  {p.free && (
                    <div data-testid={`simon-free-${p.product_key}`} className="mt-2 mb-1 inline-flex items-center gap-1.5 self-start rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 text-xs font-bold text-emerald-300">
                      <Lightning size={13} weight="fill" /> Free/Drip Credits: {p.free}
                    </div>
                  )}
                  <button
                    data-testid={`subscribe-${p.product_key}`}
                    onClick={() => startSubscription(p)}
                    disabled={busy === p.product_key}
                    className="mt-7 flex items-center justify-center gap-2 font-mono-p text-sm uppercase tracking-widest py-3 font-bold bg-cyan-400 text-black hover:bg-cyan-300 transition-colors disabled:opacity-50"
                  >
                    {busy === p.product_key ? "Redirecting..." : <>Subscribe <ArrowRight size={15} weight="bold" /></>}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Credit top-up packs */}
        {topups.length > 0 && (
          <div className="mt-16">
            <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-amber-400 mb-3">// Credit Top-Ups</p>
            <h2 className="font-mono-p font-bold uppercase tracking-tight text-2xl sm:text-3xl">One-time credit packs</h2>
            <p className="mt-2 text-sm text-zinc-500 max-w-xl">Top up extra API credits anytime — one-time purchase, no subscription.</p>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {topups.map((p, i) => (
                <div key={p.product_key} data-testid={`simon-topup-${p.product_key}`} className="relative flex flex-col border border-amber-400/30 bg-amber-400/5 p-7 fadeup" style={{ animationDelay: `${i * 50}ms` }}>
                  <div className="flex items-center gap-2"><Lightning size={18} weight="fill" className="text-amber-400" /><h3 className="font-mono-p font-bold uppercase tracking-tight text-lg">{p.name}</h3></div>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="font-mono-p font-extrabold text-4xl text-amber-400">${p.price}</span>
                    <span className="text-zinc-600 text-sm mb-1">once</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-amber-400 mt-0.5 shrink-0" /> +{p.credits} API credits</li>
                    <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-amber-400 mt-0.5 shrink-0" /> One-time payment</li>
                    <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-amber-400 mt-0.5 shrink-0" /> Never expires</li>
                  </ul>
                  <button
                    data-testid={`topup-${p.product_key}`}
                    onClick={() => simonBuy(p.product_key)}
                    disabled={busy === p.product_key}
                    className="mt-7 flex items-center justify-center gap-2 font-mono-p text-sm uppercase tracking-widest py-3 font-bold bg-amber-400 text-black hover:bg-amber-300 transition-colors disabled:opacity-50"
                  >
                    {busy === p.product_key ? "Redirecting..." : <>Buy Credits <ArrowRight size={15} weight="bold" /></>}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI iD Card — Additional Build (one-time unlimited unlock) */}
        {buildUnlock && (
          <div className="mt-16">
            <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-3">// AI iD Card Builds</p>
            <h2 className="font-mono-p font-bold uppercase tracking-tight text-2xl sm:text-3xl">Additional Builds</h2>
            <p className="mt-2 text-sm text-zinc-500 max-w-xl">One-time purchase that unlocks <span className="text-cyan-400 font-semibold">unlimited</span> AI iD Card builds on your account — permanently.</p>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <div data-testid="simon-build-unlock" className="relative flex flex-col border border-cyan-400/30 bg-[#0b0e13] p-7 fadeup">
                <div className="flex items-center gap-2"><Rocket size={18} weight="fill" className="text-cyan-400" /><h3 className="font-mono-p font-bold uppercase tracking-tight text-lg">{buildUnlock.name}</h3></div>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-mono-p font-extrabold text-4xl text-cyan-400">${buildUnlock.price.toFixed(2)}</span>
                  <span className="text-zinc-600 text-sm mb-1">once</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" /> Unlimited Additional Builds</li>
                  <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" /> One-time payment — never expires</li>
                  <li className="flex items-start gap-2 text-sm text-zinc-400"><Check size={16} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" /> Unlocked permanently on your account</li>
                </ul>
                {user?.unlimited_builds ? (
                  <div data-testid="build-unlock-active" className="mt-7 flex items-center justify-center gap-2 font-mono-p text-sm uppercase tracking-widest py-3 font-bold border border-emerald-400/40 text-emerald-300">
                    <Check size={15} weight="bold" /> Unlocked
                  </div>
                ) : (
                  <button
                    data-testid="buy-build-unlock"
                    onClick={() => simonBuy(buildUnlock.product_key)}
                    disabled={busy === buildUnlock.product_key}
                    className="mt-7 flex items-center justify-center gap-2 font-mono-p text-sm uppercase tracking-widest py-3 font-bold bg-cyan-400 text-black hover:bg-cyan-300 transition-colors disabled:opacity-50"
                  >
                    {busy === buildUnlock.product_key ? "Redirecting..." : <>Unlock Builds <ArrowRight size={15} weight="bold" /></>}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Subscription confirmation (required checkbox) */}
      {confirmProduct && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="sub-confirm-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmProduct(null)} />
          <div className="relative w-full max-w-md bg-[#0b0e13] border border-cyan-400/40 p-7">
            <button onClick={() => setConfirmProduct(null)} data-testid="sub-confirm-close" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20} weight="bold" /></button>
            <div className="flex items-center gap-2 text-amber-400 mb-3"><WarningCircle size={22} weight="fill" /><span className="font-mono-p font-bold uppercase tracking-widest text-sm">Confirm Subscription</span></div>
            <h3 className="font-mono-p font-bold text-lg text-white">{confirmProduct.name} — ${confirmProduct.price}/mo</h3>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
              S.I.M.O.N. is a <span className="text-white font-semibold">monthly subscription</span>. Your subscription renews every month. If your subscription lapses, <span className="text-amber-400 font-semibold">your account will be suspended</span> until payment is restored.
            </p>
            <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="sub-agree-checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-cyan-400 shrink-0"
              />
              <span className="text-sm text-zinc-300">I understand this is a recurring monthly subscription and that my account will be suspended if the subscription lapses.</span>
            </label>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setConfirmProduct(null)} data-testid="sub-cancel-btn" className="flex-1 font-mono-p text-sm uppercase tracking-widest py-3 border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors">Cancel</button>
              <button
                onClick={confirmSubscription}
                disabled={!agreed}
                data-testid="sub-proceed-btn"
                className="flex-1 font-mono-p text-sm uppercase tracking-widest py-3 font-bold bg-cyan-400 text-black hover:bg-cyan-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
